import { useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { LuHouse } from 'react-icons/lu';
import type { HomeResponse } from '@/client';
import { useHomes } from '@/contexts/HomesContext';
import { homeIllustration } from '@/screens/home/home-illustrations';
import { SwitcherMenu, type SwitcherItem, type SwitcherVariant } from './SwitcherMenu';

interface HomeSwitcherProps {
    home?: HomeResponse | null;
    variant?: SwitcherVariant;
    prefix?: React.ReactNode;
}

const homeLabel = (home: HomeResponse) => home.name?.trim() || home.address;

export function HomeSwitcher({ home, variant = 'hero', prefix }: HomeSwitcherProps) {
    const intl = useIntl();
    const navigate = useNavigate();
    const { homes } = useHomes();

    if (!home) return null;

    const items: SwitcherItem[] = homes.map((entry) => {
        const place = [entry.city, entry.state].filter(Boolean).join(', ');
        const memberCount = (entry as HomeResponse & { member_count?: number }).member_count;

        const members =
            memberCount === undefined
                ? null
                : intl.formatMessage(
                    {
                        id: 'd40a7f18-5b92-4c36-8e07-1f6b3a9c5d82',
                        defaultMessage: '{count, plural, one {# member} other {# members}}',
                    },
                    { count: memberCount },
                );

        return {
            id: entry.id,
            label: homeLabel(entry),
            meta: [place, members].filter(Boolean).join(' · ') || undefined,
            artwork: homeIllustration(entry),
        };
    });

    return (
        <SwitcherMenu
            variant={variant}
            prefix={prefix}
            icon={LuHouse}
            currentId={home.id}
            currentLabel={homeLabel(home)}
            items={items}
            onSelect={(id) => navigate(`/home/${id}`)}
            addLabel={intl.formatMessage({
                id: 'a5d719c0-4e83-4b26-9f01-7c3d5b8e2a64',
                defaultMessage: 'Add home',
            })}
            onAdd={() => navigate('/onboarding?flow=home')}
        />
    );
}
